import { supabase } from './supabase';

export interface Notification {
  id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  read: boolean;
  created_at: string;
}

export interface NotificationPreferences {
  email_enabled: boolean;
  push_enabled: boolean;
  preferences: {
    trip_updates: boolean;
    comments: boolean;
    invitations: boolean;
    reminders: boolean;
    weather_alerts: boolean;
  };
}

export async function getNotifications(limit = 20): Promise<Notification[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
}

export async function getUnreadCount(): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('read', false);

  if (error) throw error;
  return count || 0;
}

export async function markAsRead(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('id', notificationId);

  if (error) throw error;
}

export async function markAllAsRead(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('notifications')
    .update({ read: true })
    .eq('user_id', user.id)
    .eq('read', false);

  if (error) throw error;
}

export async function deleteNotification(notificationId: string): Promise<void> {
  const { error } = await supabase
    .from('notifications')
    .delete()
    .eq('id', notificationId);

  if (error) throw error;
}

export async function createNotification(
  userId: string,
  type: string,
  title: string,
  message: string,
  data?: any
): Promise<void> {
  const { error} = await supabase
    .from('notifications')
    .insert({
      user_id: userId,
      type,
      title,
      message,
      data,
    });

  if (error) throw error;
}

export async function getNotificationPreferences(): Promise<NotificationPreferences | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('notification_preferences')
    .select('*')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) throw error;

  if (!data) {
    const defaultPrefs: NotificationPreferences = {
      email_enabled: true,
      push_enabled: true,
      preferences: {
        trip_updates: true,
        comments: true,
        invitations: true,
        reminders: true,
        weather_alerts: true,
      },
    };

    const { error: insertError } = await supabase
      .from('notification_preferences')
      .insert({
        user_id: user.id,
        ...defaultPrefs,
      });

    if (insertError) throw insertError;

    return defaultPrefs;
  }

  return data as NotificationPreferences;
}

export async function updateNotificationPreferences(
  preferences: Partial<NotificationPreferences>
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('notification_preferences')
    .upsert({
      user_id: user.id,
      ...preferences,
    }, {
      onConflict: 'user_id',
    });

  if (error) throw error;
}

export async function scheduleReminder(
  tripId: string,
  reminderDate: Date,
  type: 'pre_trip' | 'packing' | 'weather' | 'custom',
  message: string
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { error } = await supabase
    .from('trip_activities')
    .insert({
      trip_id: tripId,
      user_id: user.id,
      activity_type: 'reminder',
      activity_data: {
        reminder_date: reminderDate.toISOString(),
        type,
        message,
      },
    });

  if (error) throw error;
}

export function subscribeToNotifications(
  onNotification: (notification: Notification) => void
): () => void {
  let channel: ReturnType<typeof supabase.channel> | null = null;

  supabase.auth.getUser().then(({ data: { user } }) => {
    if (!user) return;

    channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          onNotification(payload.new as Notification);
        }
      )
      .subscribe();
  });

  return () => {
    if (channel) channel.unsubscribe();
  };
}

export async function requestPushPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}

export function showBrowserNotification(title: string, body: string, icon?: string): void {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: icon || '/favicon.ico',
      badge: '/favicon.ico',
    });
  }
}
